import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const NotificationItem = styled(Flex)`
  background: ${(props) => (props.$isRead ? theme.colors.NEU_0 : theme.colors.NEU_100)};
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  ${(props) =>
    !props.$isRead &&
    `
    border-left: 3px solid ${props.$brandColor || theme.colors.PRIMARY};
  `}

  &:hover {
    background: ${theme.colors.NEU_100};
    box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.08)};
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => toRgba(props.$brandColor || theme.colors.PRIMARY, 0.1)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UnreadBadge = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  position: absolute;
  top: 12px;
  right: 12px;
`;

const EmptyState = styled(Flex)`
  padding: 40px 20px;
  text-align: center;
`;

/**
 * NotificationsWidget Component
 *
 * Displays recent notifications and activity for the student.
 * Shows notifications such as:
 * - New messages in discussion modules
 * - New feedback on submitted tasks
 * - Course updates and new content
 * - Mentions and replies
 *
 * Features:
 * - Visual distinction between read/unread notifications
 * - Timestamp display with relative time
 * - Click to navigate to relevant content
 * - Icon-based notification types
 */
const NotificationsWidget = ({ notifications = [] }) => {
  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleNotificationClick = (notification) => {
    // TODO: Navigate to the notification link
    console.log('Navigate to:', notification.link);
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (!notifications || notifications.length === 0) {
    return (
      <WidgetContainer direction='column' align='flex-start'>
        <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
          <Flex direction='column' align='flex-start'>
            <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
              Recent Activity
            </Typo.ContentSmallHeader>
            <Typo.Info color={theme.colors.NEU_600}>Stay updated with your courses</Typo.Info>
          </Flex>
          <Icon.Notifications size={'medium'} color={theme.colors.PRIMARY} />
        </Flex>

        <EmptyState direction='column' align='center' width='100%'>
          <Icon.Notifications size={'large'} color={theme.colors.NEU_300} style={{ marginBottom: '16px' }} />
          <Typo.MediumText color={theme.colors.NEU_600}>No notifications yet</Typo.MediumText>
          <Typo.Info color={theme.colors.NEU_600} style={{ marginTop: '8px' }}>
            You'll see updates about your courses here
          </Typo.Info>
        </EmptyState>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
        <Flex direction='column' align='flex-start'>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Recent Activity
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>
            {unreadCount > 0 ? `${unreadCount} new ${unreadCount === 1 ? 'notification' : 'notifications'}` : 'All caught up'}
          </Typo.Info>
        </Flex>
        <Icon.Notifications size={'medium'} color={theme.colors.PRIMARY} />
      </Flex>

      <NotificationsList>
        {notifications.map((notification) => {
          const NotificationIcon = Icon[notification.icon] || Icon.Notifications;

          return (
            <NotificationItem
              key={notification.id}
              align='flex-start'
              gap='12'
              $isRead={notification.isRead}
              $brandColor={theme.colors.PRIMARY}
              onClick={() => handleNotificationClick(notification)}
            >
              {!notification.isRead && <UnreadBadge $brandColor={theme.colors.PRIMARY} />}

              <IconWrapper $brandColor={theme.colors.PRIMARY}>
                <NotificationIcon size={'small'} color={theme.colors.PRIMARY} />
              </IconWrapper>

              <Flex direction='column' align='flex-start' gap='4' style={{ flex: 1 }}>
                <Typo.MediumText fontWeight={notification.isRead ? '400' : '600'} color={theme.colors.NEU_PRIMARY}>
                  {notification.message}
                </Typo.MediumText>

                <Flex align='center' gap='8'>
                  <Typo.Info color={theme.colors.NEU_600} style={{ fontSize: '12px' }}>
                    {notification.courseTitle}
                  </Typo.Info>
                  <Typo.Info color={theme.colors.NEU_600} style={{ fontSize: '12px' }}>
                    •
                  </Typo.Info>
                  <Typo.Info color={theme.colors.NEU_600} style={{ fontSize: '12px' }}>
                    {getRelativeTime(notification.timestamp)}
                  </Typo.Info>
                </Flex>
              </Flex>
            </NotificationItem>
          );
        })}
      </NotificationsList>
    </WidgetContainer>
  );
};

export default NotificationsWidget;
